import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../context/ThemeContext";
import { auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { authenticatedFetch } from "../services/apiClient";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CreateApiKey() {
  const [apiKeyList, setApiKeyList] = useState([]);
  const [newKeyData, setNewKeyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);
  const navigate = useNavigate();

  // ---------------------------
  // Load existing API keys
  // ---------------------------
  async function loadApiKeys(preserveNewSecret = false) {
    setLoading(true);
    setApiKeyList([]);
    if (!preserveNewSecret) {
      setNewKeyData(null);
    }
    
    try {
      const user = auth.currentUser;
      if (!user) {
        navigate("/login");
        return;
      }

      const res = await authenticatedFetch(
        `${BACKEND_URL}/api/apikey/list?userId=${user.uid}`
      );
      const data = await res.json();

      if (data.success && data.keys.length > 0) {
        setApiKeyList(data.keys); // Store all keys
      }
    } catch (err) {
      console.error("Failed to load keys:", err);
    }
    setLoading(false);
    setInitialLoadDone(true); // <-- API finished loading
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthReady(true);
      if (!user) {
        navigate("/login");
        return;
      }
      loadApiKeys();
    });
    return () => unsubscribe();
  }, [navigate]);

  // ---------------------------
  // Create new API Key
  // ---------------------------
  async function handleGenerate() {
    setLoading(true);
    
    try {
      const user = auth.currentUser;
      if (!user) {
        navigate("/login");
        return;
      }

      const res = await authenticatedFetch(`${BACKEND_URL}/api/apikey/create`, {
        method: "POST",
        body: JSON.stringify({ userId: user.uid }),
      });

      const data = await res.json();

      if (data.success) {
        setNewKeyData(data.data);
        await loadApiKeys(true);
      }
    } catch (err) {
      console.error("API key generation failed:", err);
      alert("Failed to create API key.");
    }
    setLoading(false);
  }

  // ---------------------------
  // Revoke/Deactivate API Key
  // ---------------------------
  async function handleRevoke(keyId) {
    if (!keyId) return;
    const confirmAction = window.confirm("Deactivate this API key? This cannot be used afterwards.");
    if (!confirmAction) return;
    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        navigate("/login");
        return;
      }

      const res = await authenticatedFetch(`${BACKEND_URL}/api/apikey/delete`, {
        method: "DELETE",
        body: JSON.stringify({ keyId }),
      });
      const data = await res.json();
      if (data.success) {
        // Refresh to reflect updated status
        await loadApiKeys(true);
      } else {
        alert(data.message || "Failed to deactivate API key.");
      }
    } catch (err) {
      console.error("Failed to deactivate API key:", err);
      alert("Failed to deactivate API key.");
    }
    setLoading(false);
  }

  // ---------------------------
  // Button Disable Logic
  // ---------------------------
  const disableButton = loading;

  return (
    <div style={{ padding: "20px", maxWidth: "800px", margin: "auto", width: "100%" }}>
      <div
        style={{
          padding: "8px 16px",
          fontSize: "14px",
          borderRadius: "4px",
          border: "1px solid #ddd",
          background: "transparent",
          marginBottom: "15px",
          visibility: "hidden",
        }}
      >
        ← Back
      </div>
      <h3 style={{ textAlign: "center", marginBottom: "25px" }}>Create API Key</h3>

      {/* Python SDK Download (styled like the secret section) */}
      <div
        style={{
          marginTop: "10px",
          marginBottom: "25px",
          color: isDarkMode ? "#ffffff" : "#000000",
        }}
      >
        <h3 style={{ color: isDarkMode ? "#ffffff" : "#000000", margin: 0 }}>
          Download Python SDK
        </h3>
        <a
          href="https://api.linesol.online/downloads/Linesol-python-sdk.zip"
          download
          style={{
            color: "#007bff",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          Linesol-python-sdk.zip
        </a>
        <p style={{ margin: "0 0 0 0", fontSize: "13px", color: isDarkMode ? "#ffffffff" : "#000000ff" }}>
          Use your API Key and Secret with the Python SDK to review categories and demographics, launch studies, and retrieve results across the full workflow with Python.
        </p>
      </div>

      {/* Generate Button */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
        <button
          onClick={handleGenerate}
          disabled={disableButton}
          style={{
            padding: "12px 0px",
            fontSize: "16px",
            whiteSpace: "nowrap",
            borderRadius: "8px",
            border: "none",
            background: disableButton ? "#b6b6b6ff" : "blue",
            color: "white",
            cursor: disableButton ? "not-allowed" : "pointer",
            transition: "0.3s",
          }}
      >
        Generate API Key
        </button>
      </div>

      {/* Show generated Secret ONCE */}
      {newKeyData && (
        <div
          style={{
            marginTop: "25px",
            color: isDarkMode ? "#ffffff" : "#000000",
          }}
        >
          <h3 style={{ color: isDarkMode ? "#ffffff" : "#000000" }}>
            Your API Key (Copy Now!)
          </h3>

          <div style={{ wordBreak: "break-all", marginBottom: "8px" }}>
            <strong>API Key:</strong> {newKeyData.apiKey}
          </div>

          <div style={{ wordBreak: "break-all" }}>
            <strong>API Secret:</strong> {newKeyData.apiSecret}
          </div>

          <p style={{ color: "red", fontWeight: "bold", marginTop: "10px" }}>
            This secret will NOT be shown again.
          </p>
        </div>
      )}

      {/* Show API Key Table */}
      {apiKeyList.length > 0 && (
        <div style={{ marginTop: "25px", width: "100%" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: "400px",
              marginLeft: "-5px",
            }}
          >
            <thead>
              <tr style={{ background: "#f1f1f1" }}>
                <th style={tableHeaderStyle}>API Key</th>
                <th style={tableHeaderStyle}>Active</th>
                <th style={tableHeaderStyle}>Created At</th>
                <th style={tableHeaderStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {apiKeyList.map((key) => (
                <tr key={key.id}>
                  <td style={tableCellStyle(isDarkMode)}>{key.apiKey}</td>
                  <td style={tableCellStyle(isDarkMode)}>{key.active ? "Yes" : "No"}</td>
                  <td style={tableCellStyle(isDarkMode)}>
                    {new Date(key.createdAt).toLocaleString()}
                  </td>
                  <td style={{ ...tableCellStyle(isDarkMode), textAlign: "center" }}>
                    {key.active ? (
                      <button
                        onClick={() => handleRevoke(key.id)}
                        disabled={loading}
                        style={{
                          padding: "6px 10px",
                          fontSize: "14px",
                          borderRadius: "6px",
                          border: "none",
                          background: loading ? "#b6b6b6" : "blue",
                          color: "white",
                          cursor: loading ? "not-allowed" : "pointer",
                        }}
                      >
                        Deactivate
                      </button>
                    ) : (
                      <span style={{ opacity: 0.7 }}>Inactive</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const tableHeaderStyle = {
  padding: "5px",
  border: "1px solid #ddd",
  textAlign: "center",
  fontWeight: "bold",
};

const tableCellStyle = (isDarkMode) => ({
  padding: "5px",
  border: "1px solid #ddd",
  wordBreak: "break-all",
  color: isDarkMode ? "#ffffff" : "#000000",
});